import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';

import { BlockFormBlockSelectedCard } from '@eika-kortbetjening/block-form-block-selected-card';
import { BlockType } from '@eika-kortbetjening/block-form-block-selected-card/lib/types';

import SkeletonList from '@/components/skeleton/SkeletonList';
import piwik from '@/piwik/piwik';
import { useCard } from '@/react-query/hooks/query/use-card';
import { isHardBlocked, isSoftBlocked } from '@/utils/card-status-utils';
import { navigateToCardDetails } from '@/utils/url/get-card-details-url-utils';
import { getHardBlockUrl, getRootUrl, navigateToHardBlock, navigateToSoftBlock } from '@/utils/url/get-url-utils';

import { useLinkProducer, useNavigate } from '@domain/app';
import useText from '@hooks/use-text';
import { canBlock, canHardBlock } from '@utils/allowed-operations/card-allowed-operations-utils';

const SelectBlockType = () => {
    const { data: card, isLoading } = useCard();
    const text = useText();
    const navigate = useNavigate();
    const linkProducer = useLinkProducer();

    const [selectedOption, selectOption] = useState<BlockType | undefined>(undefined);
    const [formError, setFormError] = useState<string | undefined>(undefined);

    useEffect(() => {
        if (!selectedOption) {
            setFormError(undefined);
        }
    }, [selectedOption]);

    if (typeof card === 'undefined' || isLoading) {
        return <SkeletonList />;
    }

    if (isSoftBlocked(card) && canHardBlock(card)) {
        // NOTE: this navigate also needs to be changed
        return <Navigate to={getHardBlockUrl(linkProducer)(card)} replace />;
    }

    if (isHardBlocked(card) || !canBlock(card)) {
        return <Navigate to={getRootUrl(linkProducer)} replace />;
    }

    const next = () => {
        if (!selectedOption) {
            setFormError(text.formPleaseFillOutAlternative);
            return;
        }

        if (selectedOption === BlockType.SOFT_BLOCK) {
            navigateToSoftBlock(navigate)(card);
            return;
        }

        if (selectedOption === BlockType.HARD_BLOCK) {
            navigateToHardBlock(navigate)(card);
        }
    };

    const cancel = () => {
        navigateToCardDetails(navigate)(card);
    };

    return (
        <BlockFormBlockSelectedCard
            selectedBlockType={selectedOption}
            setSelectedBlockType={selectOption}
            selectedBlockTypeError={formError}
            next={next}
            cancel={() => cancel()}
            sitecoreDictionary={text}
            piwik={piwik}
        />
    );
};

export default SelectBlockType;
